/**
 * The shapes the API speaks, as `service/SPEC.md` defines them.
 *
 * The enums are const tuples rather than bare unions so the UI can iterate
 * them in the one canonical order (a picker, a section list) without keeping a
 * second copy that drifts. Formats and channels live in `domain/formats.ts`,
 * with the channels each format defaults to.
 */
import type { Channel, FilmedChannel, Format } from '../domain/formats'

export { CHANNELS, FILMED_CHANNELS, FORMATS } from '../domain/formats'
export type { Channel, FilmedChannel, Format } from '../domain/formats'

export const SERIES = ['profit', 'agents', 'books'] as const
export type Series = (typeof SERIES)[number]

export const STATUSES = ['idea', 'scripted', 'filmed', 'edited', 'published', 'killed'] as const
export type Status = (typeof STATUSES)[number]

export type Source = 'phone' | 'claude' | 'bot' | 'comment' | 'research' | 'import' | (string & {})

/**
 * One beat of a video's script: what Martin says, and what is on screen
 * while he says it. `kind` is the beat's label ("Hook", "Answer"), which
 * starts from the format's template and is otherwise free text.
 */
export interface Beat {
  kind: string
  say: string
  screen: string
}

/** Where an idea's card sits inside its stage's frame on the desktop canvas. */
export interface CanvasPoint {
  x: number
  y: number
}

export interface Idea {
  id: number
  title: string
  series: Series
  format: Format
  /** Never empty, in the order `CHANNELS` lists them. */
  channels: Channel[]
  /**
   * Where it is out, per channel: a subset of `channels`. `status` is the video
   * pipeline, and LinkedIn's posting lives only here.
   */
  posted_to: Channel[]
  status: Status
  body: string
  /** Empty until Martin writes something: the editor shows the format's template meanwhile. */
  script: Beat[]
  /**
   * Null until the card is placed by hand. The server forgets it when the
   * status changes, because the point belongs to the frame it was set in.
   */
  canvas: CanvasPoint | null
  source: Source
  /**
   * The stored video per channel, each a path under the server's REELS_DIR.
   * The path is only good for telling one upload from the next: the file is
   * played from `/api/ideas/{id}/reels/{channel}` (see `reelSource`).
   */
  reels: Partial<Record<FilmedChannel, string>>
  published_url: string | null
  /** What the video carries when it goes out, empty until written (limits in `POST_TEXT`). */
  short_caption: string
  youtube_title: string
  youtube_description: string
  position: number
  created_at: string
  updated_at: string
}

export type EventKind =
  | 'created'
  | 'status'
  | 'edited'
  | 'batched'
  | 'reel'
  | 'reel_removed'
  | 'channels'
  | 'posted'
  | 'unposted'

export interface IdeaEvent {
  idea_id: number
  at: string
  kind: EventKind
  detail: string
}

/** `GET /api/ideas/{id}`: the idea plus its activity log. */
export interface IdeaWithEvents extends Idea {
  events: IdeaEvent[]
}

export interface Batch {
  id: number
  name: string
  created_at: string
  filmed_at: string | null
  idea_ids: number[]
}

export interface IdeaQuery {
  status?: Status
  series?: Series
  format?: Format
  channel?: Channel
  q?: string
}

export interface NewIdea {
  title: string
  series?: Series
  format?: Format
  channels?: Channel[]
  body?: string
  source?: Source
}

export type IdeaPatch = Partial<
  Pick<
    Idea,
    | 'title'
    | 'series'
    | 'format'
    | 'channels'
    | 'posted_to'
    | 'status'
    | 'body'
    | 'script'
    | 'canvas'
    | 'published_url'
    | 'short_caption'
    | 'youtube_title'
    | 'youtube_description'
    | 'position'
  >
>

/** One card's new place, or `null` to hand it back to the automatic layout. */
export interface Placement {
  id: number
  canvas: CanvasPoint | null
}

export interface NewBatch {
  idea_ids: number[]
  name?: string
}

export interface BatchPatch {
  idea_ids?: number[]
  filmed?: true
}

export interface MatchResult {
  question: string
  idea_id: number | null
  score: number
}

/** Everything the app asks of the server. The real client and the mock both implement it. */
export interface Api {
  listIdeas(query?: IdeaQuery): Promise<Idea[]>
  createIdea(input: NewIdea): Promise<Idea>
  getIdea(id: number): Promise<IdeaWithEvents>
  updateIdea(id: number, patch: IdeaPatch): Promise<Idea>
  /** Many cards at once, all or nothing: a drag that pins a frame's layout, or Tidy up. */
  arrange(placements: Placement[]): Promise<Idea[]>
  deleteIdea(id: number): Promise<void>
  createBatch(input: NewBatch): Promise<Batch>
  getBatch(id: number): Promise<Batch>
  updateBatch(id: number, patch: BatchPatch): Promise<Batch>
  listBatches(options?: { open?: boolean }): Promise<Batch[]>
  match(questions: string[]): Promise<MatchResult[]>
}
